import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, NavLink } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Check, Sparkles, ArrowRight } from 'lucide-react';
import { billingApi, type ApiPlan, type BillingTerm } from '../../api';
import { planItemLabels } from '../../lib/planEntitlements';
import { formatPlanPrice, getPricingOptions, resolvePlanPrice, TERM_LABELS } from '../../lib/planPricing';
import { LandingSectionShell } from './LandingSectionShell';

const ALL_TERMS: BillingTerm[] = ['monthly', 'halfyearly', 'yearly'];

function availableTerms(plans: ApiPlan[]): BillingTerm[] {
  const set = new Set<BillingTerm>();
  plans.forEach((p) => getPricingOptions(p).forEach((o) => set.add(o.term)));
  return ALL_TERMS.filter((t) => set.has(t));
}

export default function PricingPlans() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<ApiPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingTerm, setBillingTerm] = useState<BillingTerm>('monthly');
  const headingRef = useRef<HTMLHeadingElement>(null);
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, margin: '-60px' });

  useEffect(() => {
    void billingApi
      .listPlans()
      .then((res) => setPlans(res.plans))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);

  const terms = useMemo(() => availableTerms(plans), [plans]);

  useEffect(() => {
    if (terms.length && !terms.includes(billingTerm)) setBillingTerm(terms[0]);
  }, [terms, billingTerm]);

  const popularId = plans.length >= 2 ? plans[Math.floor(plans.length / 2)]?.planId : plans[0]?.planId;

  const goRegister = (planId: string) => {
    navigate(`/register?plan=${encodeURIComponent(planId)}&term=${encodeURIComponent(billingTerm)}`);
  };

  return (
    <LandingSectionShell id="pricing">
      <div ref={sectionRef} className="container mx-auto px-4">
        <div className="mx-auto mb-6 flex flex-wrap items-center justify-center gap-3">
          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-emerald-300/95">
            Live plans · Razorpay checkout
          </span>
          <span className="text-xs text-slate-500">Pick duration → register → pay → workspace</span>
        </div>

        <h2 ref={headingRef} className="section-heading-saas--on-dark">
          Plans built for finance shops
        </h2>
        <p className="mx-auto mb-8 max-w-2xl text-center text-slate-400">
          Every tier is configured in the super-admin console with module access and granular capabilities.
          Choose 1 month, 3 months, or 1 year — prices are set per plan and duration.
        </p>

        {!loading && terms.length > 1 ? (
          <div className="mx-auto mb-12 flex max-w-md flex-wrap justify-center gap-2">
            {terms.map((term) => {
              const on = billingTerm === term;
              return (
                <button
                  key={term}
                  type="button"
                  onClick={() => setBillingTerm(term)}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                    on
                      ? 'border-emerald-400/60 bg-emerald-500/20 text-emerald-100'
                      : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/20 hover:text-white'
                  }`}
                >
                  {TERM_LABELS[term]}
                </button>
              );
            })}
          </div>
        ) : null}

        {loading ? (
          <p className="text-center text-sm text-slate-500">Loading plans…</p>
        ) : plans.length === 0 ? (
          <div className="col-span-full rounded-2xl border border-dashed border-white/15 bg-white/[0.03] px-6 py-12 text-center text-sm text-slate-400">
            Plans are being configured.{' '}
            <NavLink to="/register" className="font-semibold text-emerald-400 hover:text-emerald-300">
              Register with a trial key
            </NavLink>
          </div>
        ) : (
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 md:grid-cols-3 md:gap-5">
            {plans.map((plan, index) => {
              const popular = plan.planId === popularId;
              const features = planItemLabels(plan).slice(0, 8);
              const price = resolvePlanPrice(plan, billingTerm);
              const termLabel = TERM_LABELS[billingTerm];
              return (
                <motion.div
                  key={plan.planId}
                  initial={{ opacity: 0, y: 26, rotateX: 8 }}
                  animate={inView ? { opacity: 1, y: 0, rotateX: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.08 * index, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -6, scale: 1.01 }}
                  className={`landing-glow-ring rounded-2xl p-[1px] ${popular ? 'md:-translate-y-2' : ''}`}
                  style={{ transformStyle: 'preserve-3d' }}
                >
                <div
                  className={`relative flex h-full flex-col rounded-2xl border p-8 backdrop-blur-xl transition-all duration-300 ${
                    popular
                      ? 'border-emerald-500/40 bg-gradient-to-b from-emerald-950/90 to-zinc-950/95 shadow-[0_24px_60px_-20px_rgba(16,185,129,0.25)]'
                      : 'border-white/10 bg-zinc-950/70 hover:border-white/20'
                  }`}
                >
                  {popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <div className="flex items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-100">
                        <Sparkles className="h-3.5 w-3.5" />
                        Recommended
                      </div>
                    </div>
                  )}

                  <div className={popular ? 'pt-1' : ''}>
                    <h3 className="font-display text-xl font-semibold tracking-tight">{plan.name}</h3>
                    <p className="mt-2 text-sm text-slate-400">{plan.description || `Full workspace access`}</p>
                    <button
                      type="button"
                      onClick={() => goRegister(plan.planId)}
                      className="mt-6 text-left transition hover:text-emerald-300"
                    >
                      {price != null ? (
                        <>
                          <span className="font-display text-4xl font-semibold tracking-tight">{formatPlanPrice(price)}</span>
                          <span className="ml-1 text-sm text-slate-500">/ {termLabel}</span>
                        </>
                      ) : (
                        <span className="text-sm text-slate-500">Not available for {termLabel}</span>
                      )}
                    </button>
                  </div>

                  <ul className="mt-8 flex-1 space-y-3">
                    {features.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-sm text-slate-300">
                        <Check className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    disabled={price == null}
                    onClick={() => goRegister(plan.planId)}
                    className={`mt-8 inline-flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                      popular
                        ? 'bg-gradient-to-b from-emerald-500 to-emerald-700 text-white shadow-lg shadow-emerald-950/30 hover:from-emerald-400 hover:to-emerald-600'
                        : 'border border-white/15 bg-white/5 text-white hover:border-emerald-500/40 hover:bg-white/10'
                    }`}
                  >
                    Get started — {plan.name}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </div>
                </motion.div>
              );
            })}
          </div>
        )}

        <p className="mx-auto mt-10 max-w-xl text-center text-sm text-slate-500">
          Secure payments via Razorpay · Auto-pay renewals ·{' '}
          <NavLink to="/login" className="font-semibold text-emerald-400 hover:text-emerald-300">
            Sign in
          </NavLink>
        </p>
      </div>
    </LandingSectionShell>
  );
}
